/**
 * Revenue - the CFO cockpit. Tabbed shell: Overview (cash-flow
 * timeline), Profitability, Clients, Outlook, Velocity, Integrity.
 * Tab state lives in the URL (?tab=...). Each tab fetches its own
 * endpoint lazily on first activation.
 */
import { useSearchParams } from "react-router-dom";
import StaffShell from "../components/staff/StaffShell";
import { QuickBooksConnect } from "../components/revenue/QuickBooksConnect";
import OverviewTab from "../components/revenue/OverviewTab";
import CashTab from "../components/revenue/CashTab";
import PnlTab from "../components/revenue/PnlTab";
import BalanceSheetTab from "../components/revenue/BalanceSheetTab";
import CreditorsTab from "../components/revenue/CreditorsTab";
import ProfitabilityTab from "../components/revenue/ProfitabilityTab";
import ExplorerTab from "../components/revenue/ExplorerTab";
import ClientsTab from "../components/revenue/ClientsTab";
import DebtorsTab from "../components/revenue/DebtorsTab";
import OutlookTab from "../components/revenue/OutlookTab";
import VelocityTab from "../components/revenue/VelocityTab";
import DivisionsTab from "../components/revenue/DivisionsTab";
import IntegrityTab from "../components/revenue/IntegrityTab";

const TABS = [
  { id: "overview", label: "Overview", component: OverviewTab },
  { id: "cash", label: "Cash", component: CashTab },
  { id: "pnl", label: "P&L", component: PnlTab },
  { id: "balance", label: "Balance Sheet", component: BalanceSheetTab },
  { id: "profitability", label: "Profitability", component: ProfitabilityTab },
  { id: "divisions", label: "Divisions", component: DivisionsTab },
  { id: "clients", label: "Clients", component: ClientsTab },
  { id: "debtors", label: "Debtors", component: DebtorsTab },
  { id: "creditors", label: "Creditors", component: CreditorsTab },
  { id: "outlook", label: "Outlook", component: OutlookTab },
  { id: "velocity", label: "Velocity", component: VelocityTab },
  { id: "explorer", label: "Explorer", component: ExplorerTab },
  { id: "integrity", label: "Integrity", component: IntegrityTab },
];

export default function Revenue() {
  const [searchParams, setSearchParams] = useSearchParams();
  const active = searchParams.get("tab") || "overview";
  const tab = TABS.find((t) => t.id === active) || TABS[0];
  const Active = tab.component;

  const setTab = (id) => {
    const next = new URLSearchParams(searchParams);
    if (id === "overview") next.delete("tab");
    else next.set("tab", id);
    setSearchParams(next, { replace: true });
  };

  return (
    <StaffShell max="wide" bodyClass="!gap-6 !pt-8 !pb-12">
      <header data-testid="revenue-header" className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className="text-[10px] font-mono tracking-[0.22em] uppercase text-[#1B4F8A] mb-1">
            Financial planning
          </p>
          <h1 className="font-serif text-4xl sm:text-5xl font-light text-slate-900 leading-tight">
            CFO Cockpit
          </h1>
          <p className="text-sm text-stone-500 mt-2 max-w-2xl leading-relaxed">
            The Work in Progress ledger, reconciled to the penny - billed history,
            margins, client quality, outlook and the health of the numbers themselves.
          </p>
        </div>
        <QuickBooksConnect />
      </header>

      <nav
        data-testid="revenue-tabs"
        className="flex flex-wrap gap-1 border-b border-stone-200 -mb-2"
      >
        {TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => setTab(t.id)}
            data-testid={`revenue-tab-${t.id}`}
            className={`px-3.5 py-2 text-sm rounded-t-lg border border-b-0 transition-colors ${
              tab.id === t.id
                ? "bg-white border-stone-200 text-[#1B4F8A] font-semibold -mb-px"
                : "bg-transparent border-transparent text-stone-500 hover:text-slate-800"
            }`}
          >
            {t.label}
          </button>
        ))}
      </nav>

      <Active />
    </StaffShell>
  );
}
